import { motion } from 'framer-motion';
import { Target, Award, ArrowRight, Map, CheckCircle, AlertCircle, DollarSign } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import MarketingLayout from '../../components/MarketingLayout';
import Breadcrumb from '../../components/Breadcrumb';
import PageHero from '../../components/PageHero';
import { getPageHeroImageUrl } from '../../config/cloudflareAssets';

export default function CompetitivePositioningPage() {
  const location = useLocation();
  const heroImageUrl = getPageHeroImageUrl(location.pathname);
  return (
    <MarketingLayout>
      <div className="bg-gray-50 border-b border-gray-200">
        <div className="container mx-auto px-4 py-3">
          <Breadcrumb
            items={[
              { label: 'Strategic Framework', path: '/strategic-framework' },
              { label: 'External Assessment', path: '/external-assessment' },
              { label: 'Competitive Positioning' },
            ]}
          />
        </div>
      </div>

      <PageHero
        heroImageUrl={heroImageUrl}
        badge={
          <span className="inline-flex items-center px-4 py-2 bg-white/90 text-blue-800 rounded-full text-sm font-semibold backdrop-blur-sm">
            <Map className="w-4 h-4 mr-2" />
            External Assessment Tool 4 of 4
          </span>
        }
        title="Give customers a clear reason to choose you"
        subtitle="Understand your place in the market and sharpen the value you offer. Build a competitive position around what customers care about and what your business can deliver."
      >
        <Link
          to="/sign-up"
          className="public-primary-button inline-flex items-center bg-blue-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:opacity-90"
        >
          Clarify your position
        </Link>
      </PageHero>

      {/* Porter's Generic Strategies */}
      <section className="public-section py-16 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={false}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-bold mb-4">
              Choose the advantage your position will rest on
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Competing well starts with a choice. Use Porter’s generic strategies to explore where
              your economics, capabilities, and customer needs support a clear position.
            </p>
          </motion.div>
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto mb-12">
            <motion.div
              initial={false}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="public-card bg-white p-8 rounded-xl border border-gray-200"
            >
              <DollarSign className="w-12 h-12 text-blue-600 mb-4" />
              <h3 className="text-2xl font-bold mb-3">Cost Leadership</h3>
              <p className="text-gray-600 mb-4">
                Be the lowest-cost provider in your industry while maintaining acceptable quality
              </p>
              <div className="space-y-3">
                <div>
                  <h4 className="font-semibold text-sm text-gray-900 mb-2">How to Win:</h4>
                  <ul className="space-y-1 text-xs text-gray-600">
                    <li className="flex items-start">
                      <CheckCircle className="w-3 h-3 text-blue-500 mr-1.5 mt-0.5 flex-shrink-0" />
                      Economies of scale and scope
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-3 h-3 text-blue-500 mr-1.5 mt-0.5 flex-shrink-0" />
                      Process optimization and automation
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-3 h-3 text-blue-500 mr-1.5 mt-0.5 flex-shrink-0" />
                      Supply chain efficiency
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-3 h-3 text-blue-500 mr-1.5 mt-0.5 flex-shrink-0" />
                      Product design for manufacturability
                    </li>
                  </ul>
                </div>
                <div className="bg-blue-50 p-3 rounded-lg">
                  <p className="text-xs text-gray-700">
                    <span className="font-semibold">Example:</span> Walmart, Southwest Airlines,
                    IKEA
                  </p>
                </div>
                <div className="bg-yellow-50 p-3 rounded-lg border-l-4 border-yellow-400">
                  <p className="text-xs text-gray-700">
                    <span className="font-semibold text-yellow-700">Warning:</span> Requires high
                    volume and operational excellence. Race to bottom if costs aren't truly lower.
                  </p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={false}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="public-card bg-white p-8 rounded-xl border border-gray-200"
            >
              <Award className="w-12 h-12 text-purple-600 mb-4" />
              <h3 className="text-2xl font-bold mb-3">Differentiation</h3>
              <p className="text-gray-600 mb-4">
                Offer unique value that customers perceive as superior and are willing to pay
                premium for
              </p>
              <div className="space-y-3">
                <div>
                  <h4 className="font-semibold text-sm text-gray-900 mb-2">How to Win:</h4>
                  <ul className="space-y-1 text-xs text-gray-600">
                    <li className="flex items-start">
                      <CheckCircle className="w-3 h-3 text-purple-500 mr-1.5 mt-0.5 flex-shrink-0" />
                      Superior product features or quality
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-3 h-3 text-purple-500 mr-1.5 mt-0.5 flex-shrink-0" />
                      Brand and reputation
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-3 h-3 text-purple-500 mr-1.5 mt-0.5 flex-shrink-0" />
                      Customer service excellence
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-3 h-3 text-purple-500 mr-1.5 mt-0.5 flex-shrink-0" />
                      Innovation and technology leadership
                    </li>
                  </ul>
                </div>
                <div className="bg-purple-50 p-3 rounded-lg">
                  <p className="text-xs text-gray-700">
                    <span className="font-semibold">Example:</span> Apple, Tesla, Patagonia
                  </p>
                </div>
                <div className="bg-yellow-50 p-3 rounded-lg border-l-4 border-yellow-400">
                  <p className="text-xs text-gray-700">
                    <span className="font-semibold text-yellow-700">Warning:</span> Differentiation
                    must be valued by customers and difficult for competitors to copy.
                  </p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={false}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="public-card bg-gradient-to-br from-green-50 to-white p-8 rounded-xl border border-green-200"
            >
              <Target className="w-12 h-12 text-green-600 mb-4" />
              <h3 className="text-2xl font-bold mb-3">Focus (Niche)</h3>
              <p className="text-gray-600 mb-4">
                Serve a specific market segment better than broad-market competitors through cost
                focus or differentiation focus
              </p>
              <div className="space-y-3">
                <div>
                  <h4 className="font-semibold text-sm text-gray-900 mb-2">How to Win:</h4>
                  <ul className="space-y-1 text-xs text-gray-600">
                    <li className="flex items-start">
                      <CheckCircle className="w-3 h-3 text-green-500 mr-1.5 mt-0.5 flex-shrink-0" />
                      Deep understanding of niche needs
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-3 h-3 text-green-500 mr-1.5 mt-0.5 flex-shrink-0" />
                      Specialized capabilities/expertise
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-3 h-3 text-green-500 mr-1.5 mt-0.5 flex-shrink-0" />
                      Customized offerings
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-3 h-3 text-green-500 mr-1.5 mt-0.5 flex-shrink-0" />
                      Relationships and trust
                    </li>
                  </ul>
                </div>
                <div className="bg-green-50 p-3 rounded-lg">
                  <p className="text-xs text-gray-700">
                    <span className="font-semibold">Example:</span> Rolex, Ferrari, specialist B2B
                    SaaS
                  </p>
                </div>
                <div className="bg-yellow-50 p-3 rounded-lg border-l-4 border-yellow-400">
                  <p className="text-xs text-gray-700">
                    <span className="font-semibold text-yellow-700">Warning:</span> Niche must be
                    large enough and have distinct needs that general players don't serve well.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>

          <motion.div
            initial={false}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="public-card max-w-4xl mx-auto bg-red-50 p-6 rounded-xl border border-red-200"
          >
            <div className="flex items-start space-x-3">
              <AlertCircle className="w-6 h-6 text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-bold text-gray-900 mb-2">
                  The Danger of "Stuck in the Middle"
                </h3>
                <p className="text-gray-700 text-sm">
                  Companies that fail to commit to one of these strategies often get "stuck in the
                  middle"—not the cheapest, not the most differentiated, not focused enough. This
                  leads to mediocre performance. You must choose your competitive basis and commit
                  resources to excel at it. Trying to be everything to everyone usually results in
                  being nothing special to anyone.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Positioning Map */}
      <section className="public-section py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <motion.div
              initial={false}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-4xl font-bold mb-6">Competitive Positioning Map</h2>
              <p className="text-xl text-gray-600">
                Visualize where you and competitors sit on key dimensions that matter to customers
              </p>
            </motion.div>

            <div className="space-y-6">
              <motion.div
                initial={false}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="public-card bg-white p-8 rounded-xl border border-gray-200"
              >
                <h3 className="text-2xl font-bold mb-6">How to Create a Positioning Map</h3>
                <div className="space-y-4">
                  {[
                    {
                      step: '1',
                      title: 'Select Two Key Dimensions',
                      description:
                        'Choose attributes that matter most to customers and differentiate competitors. Examples: Price vs. Quality, Features vs. Ease of Use, Specialization vs. Breadth, Innovation vs. Reliability.',
                      tip: "Pick dimensions where you have or can build advantage. Avoid dimensions where you're weak.",
                    },
                    {
                      step: '2',
                      title: 'Plot Competitors',
                      description:
                        'Place each major competitor on the map based on customer perception (not your internal view). Where are they clustered? Where are the gaps?',
                      tip: 'Use customer surveys or interviews to validate positioning perceptions rather than guessing.',
                    },
                    {
                      step: '3',
                      title: 'Find White Space',
                      description:
                        'Look for underserved positions where customer needs exist but no strong competitor dominates. These are positioning opportunities.',
                      tip: "Not all white space is valuable. Some positions are empty because customers don't want them (low price + low quality).",
                    },
                    {
                      step: '4',
                      title: 'Choose Your Position',
                      description:
                        'Based on your strengths, customer needs, and competitive gaps, decide where you want to be perceived. This becomes your positioning target.',
                      tip: 'Your chosen position must be: valuable to customers, authentic to your strengths, and defensible against competitive response.',
                    },
                    {
                      step: '5',
                      title: 'Align Everything to Support Position',
                      description:
                        "Positioning isn't just marketing messaging—it requires product, pricing, distribution, service, and operations to deliver on the promise.",
                      tip: 'Test if every major decision supports your positioning. If something contradicts it, either change the decision or rethink your position.',
                    },
                  ].map((item, idx) => (
                    <div key={idx} className="flex items-start space-x-4 p-4 bg-gray-50 rounded-lg">
                      <div className="bg-blue-600 text-white w-10 h-10 rounded-full flex items-center justify-center font-bold flex-shrink-0">
                        {item.step}
                      </div>
                      <div className="flex-1">
                        <h4 className="font-bold mb-1">{item.title}</h4>
                        <p className="text-sm text-gray-700 mb-2">{item.description}</p>
                        <div className="bg-blue-50 p-2 rounded border-l-4 border-blue-400">
                          <p className="text-xs text-gray-700">
                            <span className="font-semibold text-blue-700">Tip:</span> {item.tip}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>

              {/* Example Positioning Map */}
              <motion.div
                initial={false}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="public-card bg-white p-8 rounded-xl border border-gray-200"
              >
                <h3 className="text-2xl font-bold mb-4">Example: CRM Software Market (2026)</h3>
                <p className="text-gray-600 mb-6 text-sm">
                  Two key dimensions: Complexity (Simple → Enterprise) vs. Price (Low → High)
                </p>
                <div className="bg-gradient-to-br from-blue-50 to-purple-50 p-8 rounded-lg">
                  <div className="grid grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <div className="bg-white p-4 rounded-lg shadow">
                        <p className="font-bold text-sm mb-1">Salesforce</p>
                        <p className="text-xs text-gray-600">Enterprise complexity, High price</p>
                        <p className="text-xs text-blue-600 mt-2">
                          Position: Enterprise leader with most features
                        </p>
                      </div>
                      <div className="bg-white p-4 rounded-lg shadow">
                        <p className="font-bold text-sm mb-1">HubSpot</p>
                        <p className="text-xs text-gray-600">Medium complexity, Medium price</p>
                        <p className="text-xs text-blue-600 mt-2">
                          Position: Growth companies, inbound focused
                        </p>
                      </div>
                    </div>
                    <div className="space-y-4">
                      <div className="bg-white p-4 rounded-lg shadow">
                        <p className="font-bold text-sm mb-1">Pipedrive</p>
                        <p className="text-xs text-gray-600">Simple, Medium-low price</p>
                        <p className="text-xs text-blue-600 mt-2">
                          Position: Sales-focused, easy to use
                        </p>
                      </div>
                      <div className="bg-white p-4 rounded-lg shadow border border-green-400">
                        <p className="font-bold text-sm mb-1">🎯 White Space Opportunity</p>
                        <p className="text-xs text-gray-600">
                          Simple + Low price for micro-businesses
                        </p>
                        <p className="text-xs text-green-600 mt-2">
                          Gap: Underserved small businesses who find existing options too
                          complex/expensive
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="mt-6 p-4 bg-blue-50 rounded-lg border-l-4 border-blue-500">
                  <p className="text-sm text-gray-700">
                    <span className="font-semibold text-blue-700">Strategic Insight:</span> While
                    Salesforce dominates enterprise and HubSpot owns growth companies, there's
                    opportunity in the "simple + affordable" quadrant for very small businesses.
                    However, this requires a different business model (self-service, low-touch
                    sales) to be profitable at lower price points.
                  </p>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Value Proposition */}
      <section className="public-section py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <motion.div
              initial={false}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-4xl font-bold mb-6">Crafting Your Value Proposition</h2>
              <p className="text-xl text-gray-600">
                Translate positioning into a compelling customer message
              </p>
            </motion.div>

            <motion.div
              initial={false}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="public-card bg-white p-8 rounded-xl border border-gray-200 mb-8"
            >
              <h3 className="text-xl font-bold mb-4">Value Proposition Formula</h3>
              <div className="bg-white p-6 rounded-lg border border-blue-300">
                <p className="text-lg font-semibold text-gray-900 mb-4">For [Target Customer]</p>
                <p className="text-lg font-semibold text-gray-900 mb-4">
                  Who [Statement of Need or Opportunity]
                </p>
                <p className="text-lg font-semibold text-gray-900 mb-4">
                  Our [Product/Service] is a [Product Category]
                </p>
                <p className="text-lg font-semibold text-gray-900 mb-4">
                  That [Key Benefit / Value]
                </p>
                <p className="text-lg font-semibold text-gray-900">
                  Unlike [Competitors], we [Key Differentiator]
                </p>
              </div>
            </motion.div>

            <div className="grid md:grid-cols-2 gap-6">
              <motion.div
                initial={false}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="public-card bg-white p-6 rounded-xl border border-green-200"
              >
                <div className="flex items-center mb-4">
                  <CheckCircle className="w-8 h-8 text-green-500 mr-3" />
                  <h3 className="text-xl font-bold">Good Value Proposition</h3>
                </div>
                <div className="bg-green-50 p-4 rounded-lg mb-4">
                  <p className="text-sm text-gray-700 italic">
                    "For fast-growing SaaS companies who struggle to align sales and marketing, our
                    revenue operations platform is an all-in-one solution that increases pipeline
                    velocity by 40%. Unlike generic CRMs or disconnected point solutions, we provide
                    end-to-end visibility and automation purpose-built for the SaaS go-to-market
                    motion."
                  </p>
                </div>
                <div className="space-y-2 text-xs">
                  <p className="flex items-start">
                    <CheckCircle className="w-3 h-3 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>
                      <span className="font-semibold">Specific target:</span> Fast-growing SaaS
                      companies
                    </span>
                  </p>
                  <p className="flex items-start">
                    <CheckCircle className="w-3 h-3 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>
                      <span className="font-semibold">Clear pain:</span> Sales/marketing
                      misalignment
                    </span>
                  </p>
                  <p className="flex items-start">
                    <CheckCircle className="w-3 h-3 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>
                      <span className="font-semibold">Quantified value:</span> 40% pipeline velocity
                      increase
                    </span>
                  </p>
                  <p className="flex items-start">
                    <CheckCircle className="w-3 h-3 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>
                      <span className="font-semibold">Clear differentiation:</span> Purpose-built
                      vs. generic
                    </span>
                  </p>
                </div>
              </motion.div>

              <motion.div
                initial={false}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="public-card bg-white p-6 rounded-xl border border-red-200"
              >
                <div className="flex items-center mb-4">
                  <AlertCircle className="w-8 h-8 text-red-500 mr-3" />
                  <h3 className="text-xl font-bold">Weak Value Proposition</h3>
                </div>
                <div className="bg-red-50 p-4 rounded-lg mb-4">
                  <p className="text-sm text-gray-700 italic">
                    "We provide innovative software solutions that help businesses succeed through
                    cutting-edge technology and world-class customer service."
                  </p>
                </div>
                <div className="space-y-2 text-xs">
                  <p className="flex items-start">
                    <AlertCircle className="w-3 h-3 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>
                      <span className="font-semibold">No target:</span> "Businesses" is too broad
                    </span>
                  </p>
                  <p className="flex items-start">
                    <AlertCircle className="w-3 h-3 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>
                      <span className="font-semibold">Vague benefit:</span> "Help succeed" means
                      nothing
                    </span>
                  </p>
                  <p className="flex items-start">
                    <AlertCircle className="w-3 h-3 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>
                      <span className="font-semibold">Generic claims:</span> "Innovative",
                      "cutting-edge", "world-class"
                    </span>
                  </p>
                  <p className="flex items-start">
                    <AlertCircle className="w-3 h-3 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>
                      <span className="font-semibold">No differentiation:</span> Could describe any
                      company
                    </span>
                  </p>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Common Mistakes */}
      <section className="public-section py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <motion.div
              initial={false}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-4xl font-bold mb-6">Common Positioning Mistakes</h2>
              <p className="text-xl text-gray-600">Avoid these positioning pitfalls</p>
            </motion.div>
            <div className="grid md:grid-cols-2 gap-6">
              {[
                {
                  mistake: 'Positioning by Internal View',
                  description:
                    'Defining position based on what you think makes you special rather than what customers care about and perceive.',
                  fix: 'Test positioning with target customers. Ask: "How would you describe us vs. competitors?" Use their language, not yours.',
                },
                {
                  mistake: 'Too Many Differentiators',
                  description:
                    "Claiming you're better at everything. Customers don't believe it and can't remember it. Leads to confused positioning.",
                  fix: 'Focus on 1-2 key differentiators that matter most to your target. Own those relentlessly. Accept trade-offs.',
                },
                {
                  mistake: 'Copying Competitor Positioning',
                  description:
                    'Following the leader\'s positioning makes you a "me too" player. You\'ll always be second-best in customer minds.',
                  fix: 'Find adjacent positioning that leverages your unique strengths. Be the alternative to the leader, not a cheaper version of them.',
                },
                {
                  mistake: "Positioning That's Not Defensible",
                  description:
                    'Claiming differentiation that competitors can easily copy in 6 months. Your position collapses when they catch up.',
                  fix: 'Build positioning on sustainable competitive advantages: proprietary tech, brand, network effects, expertise, relationships.',
                },
                {
                  mistake: 'Misalignment Between Position and Delivery',
                  description:
                    'Promising "premium quality" but delivering mediocre products. Positioning promise must match actual customer experience.',
                  fix: 'Audit whether product, service, pricing, and operations actually deliver on positioning. Fix gaps or change positioning.',
                },
                {
                  mistake: 'Not Evolving Position Over Time',
                  description:
                    "Markets change. Customer needs evolve. Competitors move. Yesterday's winning position becomes tomorrow's commodity.",
                  fix: "Review positioning annually. Monitor if customers' perception matches intended position. Refresh as needed.",
                },
              ].map((item, idx) => (
                <motion.div
                  key={idx}
                  initial={false}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="public-card bg-white p-6 rounded-xl border border-red-200"
                >
                  <div className="flex items-start space-x-3 mb-3">
                    <AlertCircle className="w-6 h-6 text-red-500 flex-shrink-0" />
                    <h3 className="text-lg font-bold text-gray-900">{item.mistake}</h3>
                  </div>
                  <p className="text-gray-700 mb-3 text-sm">{item.description}</p>
                  <div className="bg-green-50 p-3 rounded-lg border-l-4 border-green-500">
                    <p className="text-sm text-gray-700">
                      <span className="font-semibold text-green-700">✓ Fix:</span> {item.fix}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Put the framework to work */}
      <section className="public-section py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-10">
              <p className="text-sm font-semibold text-blue-700 mb-3">
                YOUR NEXT STEP IN STRATPLAN
              </p>
              <h2 className="text-3xl font-bold mb-5">
                Create a position your whole strategy can support
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Bring this framework into your company’s plan. Work through the questions, record
                your decisions, and connect them to the strategy you are building.
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="public-card bg-white p-6 rounded-xl border border-gray-200">
                <p className="text-sm font-semibold text-blue-700 mb-3">01</p>
                <h3 className="text-xl font-bold mb-3">Map the alternatives</h3>
                <p className="text-gray-600">
                  Compare the options customers consider and the attributes that influence their
                  choice.
                </p>
              </div>
              <div className="public-card bg-white p-6 rounded-xl border border-gray-200">
                <p className="text-sm font-semibold text-blue-700 mb-3">02</p>
                <h3 className="text-xl font-bold mb-3">Name your difference</h3>
                <p className="text-gray-600">
                  State the customer benefit your business can deliver with credible evidence.
                </p>
              </div>
              <div className="public-card bg-white p-6 rounded-xl border border-gray-200">
                <p className="text-sm font-semibold text-blue-700 mb-3">03</p>
                <h3 className="text-xl font-bold mb-3">Check the fit</h3>
                <p className="text-gray-600">
                  Make sure your capabilities, pricing, and strategic priorities support the promise
                  you make.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related Topics */}
      <section className="public-section py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl font-bold mb-8 text-center">Related Strategic Tools</h2>
            <div className="grid md:grid-cols-3 gap-6">
              <Link
                to="/external-assessment/market-analysis"
                className="public-card bg-white p-6 rounded-xl border border-gray-200 hover:border-gray-200 transition-all group"
              >
                <h3 className="text-xl font-bold mb-2 text-blue-600">Market Analysis</h3>
                <p className="text-gray-600 text-sm mb-3">
                  Understand market size and customer segments before defining positioning
                </p>
                <div className="flex items-center text-blue-600 font-semibold text-sm group-hover:translate-x-1 transition-transform">
                  Learn More <ArrowRight className="w-4 h-4 ml-2" />
                </div>
              </Link>
              <Link
                to="/strategy-formulation/competitive-strategy"
                className="public-card bg-white p-6 rounded-xl border border-gray-200 hover:border-gray-200 transition-all group"
              >
                <h3 className="text-xl font-bold mb-2 text-purple-600">Competitive Strategy</h3>
                <p className="text-gray-600 text-sm mb-3">
                  Translate positioning into specific strategic initiatives and actions
                </p>
                <div className="flex items-center text-purple-600 font-semibold text-sm group-hover:translate-x-1 transition-transform">
                  Learn More <ArrowRight className="w-4 h-4 ml-2" />
                </div>
              </Link>
              <Link
                to="/internal-assessment"
                className="public-card bg-white p-6 rounded-xl border border-gray-200 hover:border-gray-200 transition-all group"
              >
                <h3 className="text-xl font-bold mb-2 text-indigo-600">Internal Assessment</h3>
                <p className="text-gray-600 text-sm mb-3">
                  Ensure your internal capabilities can deliver on your positioning promise
                </p>
                <div className="flex items-center text-indigo-600 font-semibold text-sm group-hover:translate-x-1 transition-transform">
                  Next Step <ArrowRight className="w-4 h-4 ml-2" />
                </div>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Navigation */}
      <section className="public-section py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/external-assessment/market-analysis"
                className="inline-flex items-center justify-center bg-white text-gray-700 px-6 py-3 rounded-lg font-semibold border border-gray-300 hover:bg-gray-50"
              >
                ← Previous: Market Analysis
              </Link>
              <Link
                to="/internal-assessment"
                className="inline-flex items-center justify-center bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700"
              >
                Next Step: Internal Assessment <ArrowRight className="w-5 h-5 ml-2" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Start your plan */}
      <section className="public-section public-cta py-16 bg-gray-50">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-5">
            Clarify your position in StratPlan
          </h2>
          <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
            Make sure your capabilities, pricing, and strategic priorities support the promise you
            make. Start with your company’s context and build a plan you can put to work.
          </p>
          <Link
            to="/sign-up"
            className="public-primary-button inline-flex items-center gap-2 bg-blue-600 text-white px-7 py-3 rounded-full font-semibold hover:bg-blue-700"
          >
            Clarify your position <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </MarketingLayout>
  );
}
